/** Heavy plant housing designed to read as one mass through frosted sheeting. */
import * as THREE from 'three';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';
import { getZone, type ZonePalette } from '@/render/palette';

const zone: ZonePalette = (() => {
  const found = getZone('cull');
  if (!found) throw new Error('plant machine: cull zone missing from the palette');
  return found;
})();

function zoneSurface(index: number): number {
  const colour = zone.surfaces[index];
  if (colour === undefined) throw new Error(`plant machine: cull surface ${String(index)} missing`);
  return colour;
}

function box(size: readonly [number, number, number], material: THREE.Material, name: string): THREE.Mesh {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(...size), material);
  mesh.name = name;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

/**
 * How far forward the guard rail pushes the centre of this machine's bounds.
 *
 * The comment below was already right about X and the same reasoning had simply
 * never been applied to Z. The housing is 1.3 m deep and the rail stands 1.35 m
 * in front of it, so the bounds run z −0.65…+1.38 and their centre sat at 0.365
 * — twelve times `asset.spec.ts`'s tolerance for "centred in X/Z", on an axis
 * nobody had measured because session 23 never ran the asset gate on this kit.
 *
 * Every part is therefore authored `MACHINE_CENTRE_OFFSET` further back, and
 * `cullCorridor.ts` puts the same amount back at the placement, so the machine
 * has not moved a millimetre in the bay. `CULL_MACHINE_AT` still means what it
 * has always meant: where the housing stands.
 */
export const MACHINE_CENTRE_OFFSET = 0.365;

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'plant-machine';
  const housingMaterial = surface('metal', zoneSurface(1));
  const motorMaterial = surface('metal', zoneSurface(2));
  const guardMaterial = surface('metal', zoneSurface(3));

  // The chute deliberately balances the machine around X=0. The housing is
  // offset left by the matching amount, so ordinary placement still treats this
  // asymmetrical silhouette as a centred asset rather than needing a scene fudge.
  const housing = box([2.2, 1.9, 1.3], housingMaterial, 'main-housing');
  housing.position.set(-0.265, 0.95, -MACHINE_CENTRE_OFFSET);
  const motor = box([0.75, 0.30, 0.50], motorMaterial, 'raised-motor-block');
  motor.position.set(-0.615, 2.05, -0.10 - MACHINE_CENTRE_OFFSET);
  group.add(housing, motor);

  // The low corner reaches y=0.35 exactly after the 45-degree rotation. This
  // prevents an apparently simple sloped box from quietly breaking the stated
  // clearance when its rectangular thickness is included in the bounds.
  const chute = box([0.92, 0.08, 0.50], motorMaterial, 'side-discharge-chute');
  chute.position.set(1.011, 0.703553, 0.10 - MACHINE_CENTRE_OFFSET);
  chute.rotation.z = -Math.PI / 4;
  group.add(chute);

  const rail = new THREE.Group();
  rail.name = 'low-forward-guard-rail';
  for (const [x, name] of [[-0.865, 'left'], [0.335, 'right']] as const) {
    const upright = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.70, 8), guardMaterial);
    upright.name = `guard-upright-${name}`;
    upright.position.set(x, 0.35, 1.35 - MACHINE_CENTRE_OFFSET);
    upright.castShadow = true;
    rail.add(upright);
  }
  const topTube = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 1.20, 8), guardMaterial);
  topTube.name = 'guard-top-tube';
  topTube.rotation.z = Math.PI / 2;
  topTube.position.set(-0.265, 0.70, 1.35 - MACHINE_CENTRE_OFFSET);
  topTube.castShadow = true;
  rail.add(topTube);
  group.add(rail);

  return group;
}

export const plantMachineAsset: AssetDefinition = {
  id: 'plant-machine',
  label: 'Cull-bay plant machine',
  description: 'Boxy processing housing, raised motor, side chute, and low guard rail for a hazed bay.',
  footprint: { width: 2.73, height: 2.20, length: 2.03 },
  create: build,
};
